import { stacks } from "@dropbox/api-v2-client";
import { TextArea } from "@dropbox/dig-components/text_fields";
import { Text } from "@dropbox/dig-components/typography";
import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { PAP_Cancel_StacksDescription } from "@mirage/analytics/events/types/cancel_stacks_description";
import { PAP_Click_StacksDescription } from "@mirage/analytics/events/types/click_stacks_description";
import {
  createUxaElementId,
  dispatchElementClicked,
} from "@mirage/analytics/uxa";
import { stackDerivePAPProps } from "@mirage/service-stacks/service/utils";
import { MAX_STACK_DESCRIPTION_LENGTH } from "@mirage/shared/util/constants";
import i18n from "@mirage/translations";
import classnames from "classnames";
import { useRef, useState } from "react";
import { useUpdateStackDescription } from "../hooks";
import styles from "./StackDescriptionEditor.module.css";

export const StackDescriptionEditor = ({
  canEdit,
  editing,
  mutationRequestId,
  onEditStatusUpdate,
  sessionId,
  stack,
}: {
  canEdit?: boolean;
  editing?: boolean;
  mutationRequestId?: string;
  onEditStatusUpdate?: (status: boolean) => void;
  sessionId?: string;
  stack: stacks.Stack | null;
}) => {
  const { reportPapEvent } = useMirageAnalyticsContext();
  const updateStackDescription = useUpdateStackDescription(
    stack,
    !!canEdit,
    mutationRequestId,
    sessionId,
  );

  const isNewStack = stack === null;
  const [isEditing, setIsEditing] = useState(editing || isNewStack);
  const initialDescription = stack?.stack_data?.description || "";
  const [value, setValue] = useState(initialDescription);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const handleStartEditing = () => {
    if (!canEdit) return;
    setIsEditing(true);
    onEditStatusUpdate?.(true);
    if (stack) {
      const papProps = stackDerivePAPProps(stack);
      reportPapEvent(PAP_Click_StacksDescription(papProps));
    }
    dispatchElementClicked(
      createUxaElementId("stack_description", { featureLine: "stacks" }),
    );
  };

  const handleCancel = () => {
    setIsEditing(false);
    setValue(initialDescription);
    onEditStatusUpdate?.(false);
    if (stack) {
      const papProps = stackDerivePAPProps(stack);
      reportPapEvent(PAP_Cancel_StacksDescription(papProps));
    }
  };

  // Save on blur or Enter
  const handleSaveAndExit = async () => {
    if (value.trim() !== initialDescription.trim()) {
      await updateStackDescription(value);
    }
    setIsEditing(false);
    onEditStatusUpdate?.(false);
  };

  if (isEditing) {
    return (
      <TextArea
        aria-label={i18n.t("edit_stack_description")}
        className={styles.stackDescriptionInput}
        maxLength={MAX_STACK_DESCRIPTION_LENGTH}
        onBlur={handleSaveAndExit}
        onChange={(e) => setValue(e.currentTarget.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSaveAndExit();
          } else if (e.key === "Escape") {
            handleCancel();
          }
        }}
        placeholder={i18n.t("stack_description_placeholder")}
        ref={inputRef}
        resizable="auto"
        rows={1}
        value={value}
      />
    );
  }

  return (
    <Text
      aria-label={i18n.t("edit_stack_description")}
      className={classnames(styles.stackDescription)}
      color="faint"
      onClick={handleStartEditing}
      onKeyDown={(e) => {
        if (canEdit && (e.key === "Enter" || e.key === " ")) {
          handleStartEditing();
        }
      }}
      role={canEdit ? "button" : undefined}
      size="large"
      tabIndex={canEdit ? 0 : undefined}
    >
      {value || i18n.t("stack_description_placeholder")}
    </Text>
  );
};
